import Spinner from "components/General-UI/Spinner";
import SoundWave from "graphics/icons/General/SoundWave";
import { safePlayBuzzSound } from "helpers/sounds";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  enumerateAllDevices,
  selectCurrentCamera,
  selectCurrentMic,
  selectCurrentSpeakers,
  selectCurrentCameraTrackId,
  startCamera,
  startMeteringMyMicLevel,
  clearCurrentCameraTrack,
  stopMeteringMyMicLevel,
  startMic,
  clearCurrentMicTrack,
  selectCurrentMicTrackId,
} from "slices/devicesSlice";
import {
  isCamOnInMediaServer,
  isMicOnInMediaServer,
  loadCallFrame,
  setAudioInputDevice,
  setAudioOutputDevice,
  setCameraInputDevice,
} from "slices/mediaServerSlice";
import { PT_USE_CAMERA, selectPreviewType } from "slices/createPictureSlice";
import DeviceIssueTip from "../../Modals/DeviceIssueTip/DeviceIssueTip";
import MicOn from "graphics/icons/MediaControls/Mic/MicOn";
import MicOff from "graphics/icons/MediaControls/Mic/MicOff";
import DeviceSelector from "./DeviceSelector/DeviceSelector";
import CamOff from "graphics/icons/MediaControls/Cam/CamOff";
import useAttachCameraTrack from "components/Hooks/useAttachCameraTrack";

const DeviceSettings = () => {
  const [allLoading, setAllLoading] = useState(true);
  const [soundExpanding, setSoundExpanding] = useState(false);

  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);

  const currentMic = useSelector(selectCurrentMic);
  const currentMicTrackId = useSelector(selectCurrentMicTrackId);
  const currentCam = useSelector(selectCurrentCamera);
  const currentCameraTrackId = useSelector(selectCurrentCameraTrackId);
  const currentSpeakers = useSelector(selectCurrentSpeakers);
  const previewType = useSelector(selectPreviewType);

  const videoElement = useRef();

  useEffect(() => {
    window.efs_debug && window.log("use effect current mic");
    if (!allLoading && currentMic) startMic();
    return () => {
      if (!isMicOnInMediaServer()) clearCurrentMicTrack();
    };
  }, [allLoading, currentMic]);

  useEffect(() => {
    window.efs_debug && window.log("use effect currentMicTrackId");
    if (currentMicTrackId) startMeteringMyMicLevel();
    return () => stopMeteringMyMicLevel();
  }, [currentMicTrackId]);

  useEffect(() => {
    window.efs_debug && window.log("use effect current cam");
    if (!allLoading && currentCam) startCamera({ height: { ideal: 350 } });
    return () => {
      if (!isCamOnInMediaServer() && !(previewType === PT_USE_CAMERA)) {
        clearCurrentCameraTrack();
      }
    };
  }, [allLoading, currentCam, previewType]);

  useAttachCameraTrack(videoElement);

  useEffect(() => {
    loadCallFrame();
    enumerateAllDevices().then(({ audioinputs, videoinputs, audiooutputs }) => {
      setAudioInputs(audioinputs);
      setVideoInputs(videoinputs);
      setAudioOutputs(audiooutputs);

      setAllLoading(false);
    });
  }, []);

  return (
    <div className="device-settings-container">
      {allLoading && (
        <div className="device-settings-loader">
          <Spinner bgDark={false} spinnerLight={false} immediateShow={true} />
        </div>
      )}
      <div className="d-flex">
        <span className="info-collumn">
          <DeviceSelector
            label={"microphone"}
            devices={audioInputs}
            currentDevice={currentMic}
            selectDevice={setAudioInputDevice}
          />
          <DeviceSelector
            label={"camera"}
            devices={videoInputs}
            currentDevice={currentCam}
            selectDevice={(deviceId) => {
              if (previewType === PT_USE_CAMERA) {
                clearCurrentCameraTrack();
              }
              setCameraInputDevice(deviceId);
            }}
          />
          <DeviceSelector
            label={"speaker"}
            devices={audioOutputs}
            currentDevice={currentSpeakers}
            selectDevice={setAudioOutputDevice}
          />
          {!allLoading && (
            <div className="sound-wave-container">
              <span>
                <SoundWave />
              </span>
              <button
                className={`growth-button test-button ${
                  soundExpanding ? "expanded" : ""
                }`}
                disabled={soundExpanding}
                onClick={() => {
                  setSoundExpanding(true);
                  setTimeout(() => {
                    setSoundExpanding(false);
                  }, 200);
                  safePlayBuzzSound();
                }}
              >
                Test Speakers
              </button>
              <span
                style={{
                  transform: "rotate(180deg)",
                  display: "inline-block",
                }}
              >
                <SoundWave />
              </span>
            </div>
          )}
          <br />
          <br />
          <div className="device-info-row" style={{ textAlign: "center" }}>
            <br />
            <DeviceIssueTip />
          </div>
        </span>
        <span className="test-collumn">
          <div className="test-row">
            {!allLoading && (
              <>
                <span className="mic-icon">
                  {currentMicTrackId ? <MicOn /> : <MicOff />}
                </span>
                <span className="d-inline-block">
                  <span className="mic-meter">
                    <div className="bar"></div>
                    <div className="bar"></div>
                    <div className="bar"></div>
                    <div className="bar"></div>
                    <div
                      id="my-mic-white"
                      className="white-coverer"
                      style={{ width: "100%" }}
                    ></div>
                  </span>
                </span>
              </>
            )}
          </div>
          <div className="test-row">
            <div
              className="video-placeholder"
              style={{ display: currentCameraTrackId ? "none" : "" }}
            >
              <CamOff />
            </div>

            <video
              style={{ display: currentCameraTrackId ? "" : "none" }}
              ref={videoElement}
              autoPlay
              muted
            ></video>
          </div>
        </span>
      </div>
    </div>
  );
};

export default DeviceSettings;
